import { ContentBlock, Typography, Button } from '@/components';
import { useIframeMessageHandler } from '@/utils';
import * as React from 'react';
import {
  useLayoutEffect,
  FC,
  useRef,
  useCallback,
  useState,
  useEffect
} from 'react';
import { useStyles } from './styles';
import SpeedTest from '@cloudflare/speedtest';
import { getUserIP } from '@/api';

const bpsToMbps = (bps) => {
  return Math.round((bps / 1000000) * 10) / 10;
};

type Measurements = ReturnType<
  Parameters<SpeedTest['onFinish']>[number]['getSummary']
>;

export const InternetConnection: FC = () => {
  const styles = useStyles();
  const [measurements, setMeasurements] = useState<null | Measurements>(null);
  const [IP, setIP] = useState<string>('');

  const engine = useRef(null);

  const refresh = useCallback(() => {
    if (engine.current) {
      setMeasurements(null);
      engine.current.restart();
    } else {
      engine.current = new SpeedTest();
      engine.current.onFinish = (results) => {
        setMeasurements(results.getSummary());
      };
    }
  }, []);

  useIframeMessageHandler();

  useLayoutEffect(() => {
    refresh();
  }, []);

  useEffect(() => {
    const getIPData = async () => {
      const IP = await getUserIP();
      setIP(IP);
    };
    getIPData();
  }, []);

  return (
    <ContentBlock hasBackButton>
      <div className={styles.header}>
        <Typography className={styles.title} component="h2">
          Internet Connection
        </Typography>
      </div>
      <div className={styles.content}>
        {measurements ? (
          <div>
            <Typography component="h4">
              Upload:{' '}
              <span className={styles.itemValue}>
                {bpsToMbps(measurements.upload)} Mbps
              </span>
            </Typography>
            <Typography component="h4">
              Download:{' '}
              <span className={styles.itemValue}>
                {bpsToMbps(measurements.download)} Mbps
              </span>
            </Typography>
            <Typography component="h4">
              Jitter:{' '}
              <span className={styles.itemValue}>
                {Math.round(measurements.jitter * 100) / 100} ms
              </span>
            </Typography>
            <Typography component="h4">
              PacketLoss:{' '}
              <span className={styles.itemValue}>
                {measurements.packetLoss} %
              </span>
            </Typography>
            <Typography component="h4">
              IP: <span className={styles.itemValue}>{IP}</span>
            </Typography>
            <Button
              text="Refresh"
              className={styles.refreshButton}
              buttonType="secondary"
              onClick={refresh}
            />
          </div>
        ) : (
          <Typography component="h6" className={styles.loadingText}>
            Evaluating network quality, please wait...
          </Typography>
        )}
      </div>
    </ContentBlock>
  );
};
